import type {
  GameEvent,
  GameStateSnapshot,
  RankExpPoint,
  RankSubmissionDraft,
  ValidatedObservation,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'

export const RANK_MINIMUM_DURATION_MS = 3_600_000
export const RANK_MAP_STALE_MS = 15_000
export const RANK_METRIC_GAP_MS = 5_000

interface ExpAccumulator {
  start: RankExpPoint | null
  end: RankExpPoint | null
  last: RankExpPoint | null
  lastSourceAt: number | null
  gain: number
  trustedDurationMs: number
}

interface MesosAccumulator {
  startValue: number | null
  endValue: number | null
  lastValue: number | null
  lastSourceAt: number | null
  gain: number
  trustedDurationMs: number
}

interface ActiveSegment {
  mapName: string
  levelBucket: number
  startedAt: number
  lastContinuousAt: number
  exp: ExpAccumulator
  mesos: MesosAccumulator
}

const emptyExp = (): ExpAccumulator => ({
  start: null,
  end: null,
  last: null,
  lastSourceAt: null,
  gain: 0,
  trustedDurationMs: 0,
})

const emptyMesos = (): MesosAccumulator => ({
  startValue: null,
  endValue: null,
  lastValue: null,
  lastSourceAt: null,
  gain: 0,
  trustedDurationMs: 0,
})

export function rankLevelBucket(level: number): number {
  return Math.floor(level / 5) * 5
}

export function normalizeRankMapName(value: string): string {
  return value
    .normalize('NFKC')
    .replace(/[\u0000-\u001f\u007f-\u009f]+/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
}

export class EfficiencySegmentTracker {
  private segment: ActiveSegment | null = null

  constructor(private readonly preset: GamePreset) {}

  reset(): void {
    this.segment = null
  }

  ingest(
    state: GameStateSnapshot,
    observation: ValidatedObservation,
    events: readonly GameEvent[] = [],
  ): RankSubmissionDraft | null {
    const mapName = state.mapName ? normalizeRankMapName(state.mapName) : ''
    const mapObservedAt = state.observedAt?.mapName ?? null
    const level = state.level

    if (!mapName || mapObservedAt === null || state.capturedAt - mapObservedAt > RANK_MAP_STALE_MS || level === null) {
      return this.finish(this.segment?.lastContinuousAt ?? state.capturedAt)
    }

    const levelBucket = rankLevelBucket(level)
    let completed: RankSubmissionDraft | null = null
    if (!this.segment || this.segment.mapName !== mapName || this.segment.levelBucket !== levelBucket) {
      completed = this.finish(this.segment?.lastContinuousAt ?? state.capturedAt)
      this.segment = {
        mapName,
        levelBucket,
        startedAt: state.capturedAt,
        lastContinuousAt: state.capturedAt,
        exp: emptyExp(),
        mesos: emptyMesos(),
      }
    } else {
      this.segment.lastContinuousAt = state.capturedAt
    }

    this.observeExp(state, observation, events)
    this.observeMesos(state, observation)
    return completed
  }

  finish(now = Date.now()): RankSubmissionDraft | null {
    const segment = this.segment
    this.segment = null
    if (!segment?.exp.start || !segment.exp.end || segment.exp.trustedDurationMs < RANK_MINIMUM_DURATION_MS) return null

    const endedAt = Math.max(segment.startedAt, Math.min(now, segment.lastContinuousAt))
    const mesos = segment.mesos
    return {
      submissionId: crypto.randomUUID(),
      presetId: this.preset.id,
      presetVersion: this.preset.version,
      mapName: segment.mapName,
      levelBucket: segment.levelBucket,
      startedAt: segment.startedAt,
      endedAt,
      exp: {
        gain: segment.exp.gain,
        trustedDurationMs: segment.exp.trustedDurationMs,
        start: segment.exp.start,
        end: segment.exp.end,
      },
      ...(mesos.startValue !== null
        && mesos.endValue !== null
        && mesos.gain >= 0
        && mesos.trustedDurationMs >= RANK_MINIMUM_DURATION_MS
        ? {
            mesos: {
              gain: mesos.gain,
              trustedDurationMs: mesos.trustedDurationMs,
              startValue: mesos.startValue,
              endValue: mesos.endValue,
            },
          }
        : {}),
    }
  }

  private observeExp(
    state: GameStateSnapshot,
    observation: ValidatedObservation,
    events: readonly GameEvent[],
  ): void {
    if (!this.segment || state.level === null || state.exp.absolute === null) return
    const hasConfirmedLevelUp = events.some((event) => event.type === 'level-up')
    const hasFreshAbsolute = observation.accepted.exp && observation.exp.value?.absolute !== null
    if (!hasFreshAbsolute && !hasConfirmedLevelUp) return

    const sourceAt = state.observedAt?.exp ?? null
    if (sourceAt === null) return
    const next: RankExpPoint = { level: state.level, absolute: state.exp.absolute }
    const metric = this.segment.exp

    if (!metric.last || metric.lastSourceAt === null) {
      metric.start = next
      metric.end = next
      metric.last = next
      metric.lastSourceAt = sourceAt
      return
    }
    if (sourceAt <= metric.lastSourceAt && !hasConfirmedLevelUp) return

    const intervalMs = sourceAt - metric.lastSourceAt
    if (intervalMs <= 0 || intervalMs > RANK_METRIC_GAP_MS) {
      metric.last = next
      metric.lastSourceAt = sourceAt
      metric.end = next
      return
    }

    const delta = this.calculateExpDelta(metric.last, next)
    if (delta === null) return
    metric.gain += delta
    metric.trustedDurationMs += intervalMs
    metric.last = next
    metric.lastSourceAt = sourceAt
    metric.end = next
  }

  private calculateExpDelta(start: RankExpPoint, end: RankExpPoint): number | null {
    if (end.level < start.level || end.level > start.level + 4) return null
    if (start.level === end.level) return end.absolute >= start.absolute ? end.absolute - start.absolute : null

    const firstRequirement = this.preset.experienceToNext(start.level)
    if (firstRequirement === null || start.absolute > firstRequirement) return null
    let gain = firstRequirement - start.absolute
    for (let level = start.level + 1; level < end.level; level += 1) {
      const requirement = this.preset.experienceToNext(level)
      if (requirement === null) return null
      gain += requirement
    }
    return gain + end.absolute
  }

  private observeMesos(state: GameStateSnapshot, observation: ValidatedObservation): void {
    if (!this.segment || !observation.accepted.mesos || state.mesos === null) return
    const sourceAt = state.observedAt?.mesos ?? null
    if (sourceAt === null) return
    const metric = this.segment.mesos

    if (metric.lastValue === null || metric.lastSourceAt === null) {
      metric.startValue ??= state.mesos
      metric.endValue = state.mesos
      metric.lastValue = state.mesos
      metric.lastSourceAt = sourceAt
      return
    }
    if (sourceAt <= metric.lastSourceAt) return

    const intervalMs = sourceAt - metric.lastSourceAt
    const delta = state.mesos - metric.lastValue
    if (intervalMs > 0 && intervalMs <= RANK_METRIC_GAP_MS && delta >= 0) {
      metric.gain += delta
      metric.trustedDurationMs += intervalMs
    }
    metric.endValue = state.mesos
    metric.lastValue = state.mesos
    metric.lastSourceAt = sourceAt
  }
}
